import { trackError } from '#api/errors.ts';
import type { ConfigOverride, ConfigOverrideKey } from '#dn-types/host.ts';

/**
 * An override the api accepts but the webclient has no editor for — either a key we've
 * intentionally kept out of the UI, or one the api gained before we caught up.
 */
export type UnknownConfigOverride = { key: string; value: unknown };

/**
 * Every key the webclient can render. Written as a Record so that adding a key to
 * {@link ConfigOverride} without adding it here is a type error.
 */
const KNOWN_CONFIG_OVERRIDE_KEYS: Record<ConfigOverrideKey, true> = {
  'definednet.dns.match_domains': true,
  'definednet.dns.resolver_addrs': true,
  'definednet.dns.search_domains': true,
  'lighthouse.advertise_addrs': true,
  'lighthouse.dns.host': true,
  'lighthouse.dns.port': true,
  'lighthouse.interval': true,
  'lighthouse.local_allow_list': true,
  'lighthouse.remote_allow_list': true,
  'lighthouse.serve_dns': true,
  'listen.batch': true,
  'listen.host': true,
  'listen.read_buffer': true,
  'listen.udp_offloads': true,
  'listen.write_buffer': true,
  'logging.format': true,
  'logging.level': true,
  'logging.timestamp_format': true,
  preferred_ranges: true,
  'punchy.delay': true,
  'punchy.respond_delay': true,
  routines: true,
  'static_map.network': true,
  'stats.host': true,
  'stats.interval': true,
  'stats.lighthouse_metrics': true,
  'stats.listen': true,
  'stats.message_metrics': true,
  'stats.namespace': true,
  'stats.path': true,
  'stats.prefix': true,
  'stats.subsystem': true,
  'stats.type': true,
  'tun.dev': true,
  'tun.disabled': true,
  'tun.mtu': true,
  'tun.tx_queue': true,
  'tun.use_offloads': true,
};

export function isKnownConfigOverrideKey(key: string): key is ConfigOverrideKey {
  return Object.hasOwn(KNOWN_CONFIG_OVERRIDE_KEYS, key);
}

/** Keys the `DNS_CONFIG_OVERRIDES` feature flag gates. */
const DNS_OVERRIDE_KEY_PREFIX = 'definednet.dns.';

export function isDNSConfigOverrideKey(key: string): boolean {
  return key.startsWith(DNS_OVERRIDE_KEY_PREFIX);
}

/**
 * Builds the `isEditable` predicate for a form whose `definednet.dns.*` fields are hidden when
 * `DNS_CONFIG_OVERRIDES` is off, so a value already set on the resource falls through to the
 * read-only unknown bucket instead of into form state no field is mounted for.
 *
 * Every form uses the same predicate so flag-off values consistently remain visible and removable
 * in the read-only unknown bucket rather than editable through one resource type but not another.
 */
export function makeIsEditableConfigOverrideKey(canEditDNSOverrides: boolean) {
  return (key: string): key is ConfigOverrideKey =>
    isKnownConfigOverrideKey(key) && (canEditDNSOverrides || !isDNSConfigOverrideKey(key));
}

/**
 * Splits overrides from the api into the ones our forms can edit and the ones they can't.
 * Forms must keep the unknown bucket out of form state — it has no editor, no validation
 * schema, and would show up as phantom dirty state — and hand it back to
 * {@link withUnknownOverrides} on save so we don't clobber it.
 *
 * @param isEditable Narrows what this form treats as editable. Defaults to every key the
 * webclient can render, but a form that hides an editor behind a feature flag must also pass a
 * predicate that rejects those keys: a key left in the known bucket with no field mounted can
 * neither be seen nor cleared, is still validated, and still claims api errors for a path that
 * renders nothing.
 */
export function partitionConfigOverrides<T extends UnknownConfigOverride>(
  overrides: readonly T[],
  isEditable: (key: string) => key is ConfigOverrideKey = isKnownConfigOverrideKey
): { known: T[]; unknown: UnknownConfigOverride[] } {
  const known: T[] = [];
  const unknown: UnknownConfigOverride[] = [];
  for (const override of overrides) {
    if (isEditable(override.key)) {
      known.push(override);
    } else {
      unknown.push(override);
    }
  }
  return { known, unknown };
}

/**
 * Re-attaches the unknown bucket to the overrides a form is about to submit, appended so the
 * indices of known overrides (which server-side validation errors refer to) stay put.
 */
export function withUnknownOverrides(
  overrides: ConfigOverride[],
  unknown: readonly UnknownConfigOverride[]
): ConfigOverride[] {
  if (unknown.length === 0) return overrides;
  trackUnknownConfigOverrides(unknown);
  // Passing values through untouched; ConfigOverride can't describe keys we don't know.
  return [...overrides, ...(unknown as ConfigOverride[])];
}

/**
 * Report drift between the api's key set and ours. Grouped by key so keys we deliberately
 * don't surface can each be silenced in Sentry without muting the rest.
 */
function trackUnknownConfigOverrides(unknown: readonly UnknownConfigOverride[]) {
  for (const key of new Set(unknown.map((o) => o.key))) {
    // A key we can render that this form gated off (see makeIsEditableConfigOverrideKey) is the
    // flag doing its job, not drift between the api's key set and ours.
    if (isKnownConfigOverrideKey(key)) continue;

    trackError(new Error(`Preserved unknown config override: ${key}`), {
      level: 'info',
      fingerprint: ['unknown-config-override', key],
      tags: { configOverrideKey: key },
    });
  }
}
